import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Button,
  Banner,
  Modal,
  ProgressBar,
  ChoiceList,
  TextField,
} from '@shopify/polaris';
import {
  usePlans,
  useSubscriptionStatus,
  useSubscribe,
  useChangeTier,
  useCancelSubscription,
  useRedeemCoupon,
} from '../hooks/useSubscription';
import { TierBadge, TIER_ICONS } from '../components/TierBadge';
import { ChatGptIcon, GeminiIcon } from '../components/PlatformIcon';
import { PageEntrance } from '../components/ui';
import type { CouponRedeemResponse, PlanDetails, SubscriptionStatus, Tier } from '../types';
import { formatDate } from '../utils/dateFormat';
import './BillingPage.css';

/**
 * T046: BillingPage — plan comparison, subscription status,
 * upgrade/downgrade/cancel actions.
 *
 * @see contracts/billing.md
 * @see documents/billing-plan-design.md
 */

const TIER_LABELS: Record<Tier, string> = {
  FREE: 'Free',
  STARTER: 'Starter',
  GROWTH: 'Growth',
  SCALE: 'Scale',
};

const TIER_ORDER: Record<Tier, number> = {
  FREE: 0,
  STARTER: 1,
  GROWTH: 2,
  SCALE: 3,
};

const TIER_TAGLINES: Record<Tier, string> = {
  FREE: 'See exactly where AI loses your products.',
  STARTER: 'Fix your worst products. Publish your feed. Get discovered.',
  GROWTH: 'Keep your full catalog covered and always fresh.',
  SCALE: 'Scale without limits — stay always AI-ready.',
};

interface TierStory {
  readonly stageLabel: string;
  readonly bestFor: string;
  readonly promise: string;
  readonly outcomeBullets: readonly string[];
}

const HERO_SELLING_POINTS = [
  'Every plan scans and scores your catalog so you can see exactly where AI shoppers lose confidence.',
  'Paid plans add monthly AI score boosts to improve weak titles, descriptions, and summaries faster.',
  'Starter adds AI fixes, a live product feed, and llms.txt so AI crawlers can discover your catalog.',
  'Growth and Scale keep your entire catalog AI-ready with real-time sync and deeper visibility tools.',
] as const;

const TIER_STORIES: Record<Tier, TierStory> = {
  FREE: {
    stageLabel: 'Diagnostic',
    bestFor: 'testing the opportunity before you invest in automation',
    promise: 'See why your products disappear, underperform, or look incomplete inside AI shopping journeys.',
    outcomeBullets: [
      'Spot the highest-impact discoverability blockers in your catalog.',
      'See enough signal to decide where upgrading will pay you back.',
    ],
  },
  STARTER: {
    stageLabel: 'Momentum',
    bestFor: 'smaller catalogs where you want fast fixes and a public feed AI crawlers can discover',
    promise: 'Fix your weakest listings and publish a structured product feed — so AI systems can find your catalog, not just your competitors\u2019.',
    outcomeBullets: [
      'Use category templates to sharpen the listings most at risk of losing AI traffic.',
      'Publish your AI product feed and llms.txt so AI crawlers index your catalog automatically.',
    ],
  },
  GROWTH: {
    stageLabel: 'Operating Plan',
    bestFor: 'stores where you want AI discoverability working for you every week',
    promise: 'This is where Zelium becomes your always-on growth system, not just a point-in-time audit.',
    outcomeBullets: [
      'Publish your AI shopping feed and llms.txt with live Shopify sync.',
      'Catch crawl and visibility issues before they drag down your important products.',
    ],
  },
  SCALE: {
    stageLabel: 'Scale Engine',
    bestFor: 'large catalogs where you need full-catalog AI readiness and maximum automation',
    promise: 'Run full-catalog AI readiness across your entire assortment with deeper intelligence and higher automation.',
    outcomeBullets: [
      'Add competitive benchmarks and deep simulations to sharpen your decisions.',
      'Get full control over category templates across your entire assortment.',
    ],
  },
};

function formatCoverage(productLimit: number): string {
  return `Scan & score up to ${productLimit} products`;
}

function formatBoostAllowance(monthlyLlmLimit: number): string {
  return monthlyLlmLimit === 0
    ? 'No AI score boosts included'
    : `${monthlyLlmLimit} AI score boosts / month`;
}

function formatHistoryWindow(historyDays: number): string {
  return `Track score movement for ${historyDays} days`;
}

function getPlanCapabilities(plan: PlanDetails): string[] {
  const capabilities = ['Catalog scoring', 'Issue detection'];

  if (plan.features.autoFix) capabilities.push('1-click score boosts');
  if (plan.features.categoryTemplates) capabilities.push('Category template library');
  if (plan.features.aiSummaries) capabilities.push('AI summary formats');
  if (plan.features.aiFeed) capabilities.push('AI shopping feed');
  if (plan.features.webhookSync) capabilities.push('Real-time Shopify sync');
  if (plan.features.crawlSimulation) capabilities.push('Crawl visibility previews');
  if (plan.features.llmsTxt) capabilities.push('llms.txt publishing');
  if (plan.features.benchmarking) capabilities.push('Competitive benchmarks');
  if (plan.features.productRescan) capabilities.push('Per-product rescans');

  return capabilities;
}

interface PlanCardProps {
  readonly plan: PlanDetails;
  readonly currentTier: Tier;
  readonly currentInterval: string;
  readonly isAnnual: boolean;
  readonly subscriptionStatus?: SubscriptionStatus;
  readonly onPlanAction: (tier: Tier) => void;
  readonly onDowngradeToFree: () => void;
  readonly isSubscribePending: boolean;
  readonly isChangePending: boolean;
}

interface PlanActionOptions {
  readonly plan: PlanDetails;
  readonly currentTier: Tier;
  readonly currentInterval: string;
  readonly subscriptionStatus?: SubscriptionStatus;
  readonly onPlanAction: (tier: Tier) => void;
  readonly onDowngradeToFree: () => void;
  readonly isSubscribePending: boolean;
  readonly isChangePending: boolean;
}

interface UpgradeActionOptions {
  readonly plan: PlanDetails;
  readonly isRecommended: boolean;
  readonly onPlanAction: (tier: Tier) => void;
  readonly isSubscribePending: boolean;
  readonly isChangePending: boolean;
}

function renderUpgradePlanAction({
  plan,
  isRecommended,
  onPlanAction,
  isSubscribePending,
  isChangePending,
}: Readonly<UpgradeActionOptions>): React.ReactNode {
  // Temporarily keep Scale as sales-assisted only until per-store enablement ships.
  if (plan.tier === 'SCALE') {
    const salesEmail = import.meta.env['VITE_SUPPORT_EMAIL'] as string | undefined ?? 'team@zelium.app';
    return (
      <div className="bill-plan-cta">
        <Button size="large" fullWidth disabled>
          Upgrade to {TIER_LABELS[plan.tier]}
        </Button>
        <div className="bill-plan-contact-sales-note">
          <Text as="p" variant="bodySm" tone="subdued">
            Contact{' '}
            <a href={`mailto:${salesEmail}?subject=Scale plan enquiry`}>
              {salesEmail}
            </a>
            {' '}to upgrade to Scale.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className="bill-plan-cta">
      <Button
        variant={isRecommended ? 'primary' : 'secondary'}
        size="large"
        fullWidth
        onClick={() => onPlanAction(plan.tier)}
        loading={isSubscribePending || isChangePending}
      >
        Upgrade to {TIER_LABELS[plan.tier]}
      </Button>
    </div>
  );
}

function getPlanActionButton({
  plan,
  currentTier,
  currentInterval,
  subscriptionStatus,
  onPlanAction,
  onDowngradeToFree,
  isSubscribePending,
  isChangePending,
}: Readonly<PlanActionOptions>): React.ReactNode {
  const isCurrent = plan.tier === currentTier && plan.interval === currentInterval;
  const isUpgrade = TIER_ORDER[plan.tier] > TIER_ORDER[currentTier];
  const isDowngrade = TIER_ORDER[plan.tier] < TIER_ORDER[currentTier] && plan.tier !== 'FREE';
  const isRecommended = plan.tier === 'GROWTH';

  if (isCurrent) {
    return (
      <div className="bill-plan-cta">
        <Button size="large" fullWidth disabled>Current plan</Button>
      </div>
    );
  }

  if (plan.tier === 'FREE') {
    if (currentTier !== 'FREE' && subscriptionStatus !== 'CANCELLED') {
      return (
        <div className="bill-plan-cta">
          <Button tone="critical" size="large" fullWidth onClick={onDowngradeToFree}>
            Downgrade to Free
          </Button>
        </div>
      );
    }

    if (currentTier !== 'FREE' && subscriptionStatus === 'CANCELLED') {
      return (
        <div className="bill-plan-cta">
          <Button size="large" fullWidth disabled>Cancelling...</Button>
        </div>
      );
    }

    return null;
  }

  if (isUpgrade) {
    return renderUpgradePlanAction({
      plan,
      isRecommended,
      onPlanAction,
      isSubscribePending,
      isChangePending,
    });
  }

  if (isDowngrade) {
    return (
      <div className="bill-plan-cta">
        <Button size="large" fullWidth onClick={() => onPlanAction(plan.tier)} loading={isChangePending}>
          Downgrade to {TIER_LABELS[plan.tier]}
        </Button>
      </div>
    );
  }

  return null;
}

function PlanCard({
  plan,
  currentTier,
  currentInterval,
  isAnnual,
  subscriptionStatus,
  onPlanAction,
  onDowngradeToFree,
  isSubscribePending,
  isChangePending,
}: Readonly<PlanCardProps>): React.JSX.Element {
  const isCurrent = plan.tier === currentTier && plan.interval === currentInterval;
  const isRecommended = plan.tier === 'GROWTH';
  const story = TIER_STORIES[plan.tier];
  const capabilities = getPlanCapabilities(plan);
  const displayMonthlyPrice = isAnnual ? plan.annualPrice : plan.price;
  const actionButton = getPlanActionButton({
    plan,
    currentTier,
    currentInterval,
    subscriptionStatus,
    onPlanAction,
    onDowngradeToFree,
    isSubscribePending,
    isChangePending,
  });

  return (
    <article
      className={`bill-plan-card${isCurrent ? ' is-current' : ''}${isRecommended ? ' is-recommended' : ''}`}
    >
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="start">
          <div className="bill-plan-stage">{story.stageLabel}</div>
          <div className="bill-plan-icon">{TIER_ICONS[plan.tier]}</div>
        </InlineStack>

        <InlineStack gap="200" blockAlign="center">
          <Text as="h3" variant="headingMd">
            {TIER_LABELS[plan.tier]}
          </Text>
          {isCurrent && <Badge>Current</Badge>}
          {isRecommended && !isCurrent && <Badge tone="success">Most Popular</Badge>}
        </InlineStack>

        <Text as="p" variant="bodySm" tone="subdued">
          {TIER_TAGLINES[plan.tier]}
        </Text>

        <Text as="p" variant="bodyMd" fontWeight="semibold">
          Best for {story.bestFor}
        </Text>

        <Text as="p" variant="bodySm" tone="subdued">
          {story.promise}
        </Text>

        <div className="bill-plan-price-block">
          <Text as="p" variant="headingXl">
            {plan.price === 0 ? 'Free' : `$${displayMonthlyPrice}/mo`}
          </Text>
          {isAnnual && plan.annualPrice > 0 && (
            <Text as="p" variant="bodySm" tone="subdued">
              Billed ${plan.annualPrice * 12}/yr
            </Text>
          )}
        </div>

        <div className="bill-plan-outcomes">
          <Text as="p" variant="bodySm" tone="subdued" fontWeight="semibold">What you unlock</Text>
          {[
            { key: 'coverage', content: formatCoverage(plan.productLimit) },
            ...(plan.monthlyLlmLimit === 0
              ? []
              : [{ key: 'boost', content: formatBoostAllowance(plan.monthlyLlmLimit) }]),
            { key: 'history', content: formatHistoryWindow(plan.historyDays) },
            ...(plan.tier === 'FREE'
              ? []
              : [{
                  key: 'ai-platform-readiness',
                  content: (
                    <span className="bill-plan-outcome-ai">
                      <span>AI Platform Readiness —</span>
                      <span className="bill-plan-outcome-ai-icon"><ChatGptIcon /></span>
                      <span>ChatGPT,</span>
                      <span className="bill-plan-outcome-ai-icon"><GeminiIcon /></span>
                      <span>Gemini</span>
                    </span>
                  ),
                }]),
            ...story.outcomeBullets.map((bullet, i) => ({ key: `story-${i}`, content: bullet })),
            ...(plan.monthlyLlmLimit === 0
              ? [{ key: 'no-boost', content: formatBoostAllowance(plan.monthlyLlmLimit) }]
              : []),
          ].map((bullet) => (
            <div key={bullet.key} className="bill-plan-outcome">
              {bullet.content}
            </div>
          ))}
        </div>

        <div className="bill-plan-capability-group">
          <Text as="p" variant="bodySm" tone="subdued" fontWeight="semibold">Included in this plan</Text>
          <div className="bill-plan-capabilities">
            {capabilities.map((capability) => (
              <Badge key={capability}>{capability}</Badge>
            ))}
          </div>
        </div>

        {actionButton}
      </BlockStack>
    </article>
  );
}

export function BillingPage(): React.JSX.Element {
  const { data: status } = useSubscriptionStatus();
  const { data: plansData } = usePlans();
  const subscribeMutation = useSubscribe();
  const changeTierMutation = useChangeTier();
  const cancelMutation = useCancelSubscription();
  const redeemCouponMutation = useRedeemCoupon();

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState<CouponRedeemResponse | null>(null);
  const [billingInterval, setBillingInterval] = useState<string[]>(['annual']);

  const handleCloseCouponModal = useCallback(() => {
    setCouponModalOpen(false);
    setCouponCode('');
    setCouponError('');
    setCouponSuccess(null);
  }, []);

  const handleRedeemCoupon = useCallback(() => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code.');
      return;
    }
    setCouponError('');
    redeemCouponMutation.mutate(couponCode.trim(), {
      onSuccess: (data) => {
        setCouponSuccess(data);
      },
      onError: (err) => {
        setCouponError(err.message ?? 'Invalid coupon code. Please try again.');
      },
    });
  }, [couponCode, redeemCouponMutation, handleCloseCouponModal]);

  // Sync toggle to actual subscription interval on first load (once only, paid plans only)
  const intervalSynced = useRef(false);
  useEffect(() => {
    if (!intervalSynced.current && status?.tier && status.tier !== 'FREE' && status?.interval) {
      setBillingInterval([status.interval === 'ANNUAL' ? 'annual' : 'monthly']);
      intervalSynced.current = true;
    }
  }, [status?.tier, status?.interval]);

  const currentTier = status?.tier ?? 'FREE';
  const isAnnual = billingInterval[0] === 'annual';
  const currentInterval = status?.interval ?? 'EVERY_30_DAYS';
  const activePlans = isAnnual && plansData?.annualPlans
    ? [
        // Always include the Free plan from monthly plans
        ...(plansData.plans?.filter((p) => p.tier === 'FREE') ?? []),
        ...plansData.annualPlans,
      ]
    : plansData?.plans ?? [];
  const sortedPlans = [...activePlans].sort((left, right) => TIER_ORDER[left.tier] - TIER_ORDER[right.tier]);
  const currentStory = TIER_STORIES[currentTier];

  const handlePlanAction = useCallback(
    (tier: Tier) => {
      const interval = isAnnual ? 'annual' : 'monthly';
      if (currentTier === 'FREE') {
        subscribeMutation.mutate({ tier, interval });
      } else {
        changeTierMutation.mutate({ tier, interval });
      }
    },
    [currentTier, isAnnual, subscribeMutation, changeTierMutation],
  );

  const handleCancel = useCallback(() => {
    cancelMutation.mutate(undefined, {
      onSuccess: () => setCancelModalOpen(false),
    });
  }, [cancelMutation]);
  return (
    <PageEntrance>
      <Page
        title="Plans & Billing"
        subtitle="Compare plans by the catalog coverage, score boosts, and automation layer you unlock."
      >
        <BlockStack gap="400">
          <div className="z-hero bill-hero">
            <div className="z-hero-content">
              <h2 className="z-hero-title">💜 Choose the plan that keeps more products ready for AI shoppers</h2>
              <p className="z-hero-subtitle">
                Every plan scans and scores your catalog so you can see where AI shoppers lose confidence. Paid plans
                add monthly AI score boosts, and Growth plus Scale add the automation layer that keeps important
                listings fresh for ChatGPT, Gemini, and Google AI Overviews.
              </p>
              <ul className="bill-hero-points">
                {HERO_SELLING_POINTS.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </div>
          </div>
          <Layout>
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <Text as="h2" variant="headingLg">
                        Your Current AI Coverage
                      </Text>
                      <Text as="p" variant="bodySm" tone="subdued">
                        {currentStory.promise}
                      </Text>
                      {status?.interval && currentTier !== 'FREE' && (
                        <InlineStack gap="300" blockAlign="center">
                          <Text as="p" variant="bodySm" tone="subdued">
                            🗓️ {status.interval === 'ANNUAL' ? 'Annual billing' : 'Monthly billing'}
                          </Text>
                          {status.currentPeriodEnd && status.status !== 'CANCELLED' && (
                            <Text as="p" variant="bodySm" tone="subdued">
                              🔄 Renews {formatDate(status.currentPeriodEnd)}
                            </Text>
                          )}
                        </InlineStack>
                      )}
                    </BlockStack>
                    <TierBadge tier={currentTier} size="large" />
                  </InlineStack>

                  {status && (
                    <BlockStack gap="200">
                      <div className="bill-quota-grid">
                        <div className="bill-quota-tile">
                          <Text as="p" variant="bodySm" tone="subdued">
                            {currentTier === 'FREE' ? 'Catalog coverage' : 'Products this billing cycle'}
                          </Text>
                          <Text as="p" variant="bodyMd">
                            {status.productCount} / {status.productLimit}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {currentTier === 'FREE'
                              ? 'Your products scanned and scored right now'
                              : status.billingCycleStart
                                ? `New products added since ${status.billingCycleStart}`
                                : 'New products added in the current billing cycle'}
                          </Text>
                          <ProgressBar
                            progress={Math.min((status.productCount / status.productLimit) * 100, 100)}
                            size="small"
                          />
                        </div>

                        <div className="bill-quota-tile">
                          <Text as="p" variant="bodySm" tone="subdued">AI score boosts used</Text>
                          <Text as="p" variant="bodyMd">
                            {status.monthlyLlmUsed} / {status.monthlyLlmLimit === 0 ? '—' : status.monthlyLlmLimit}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            AI-assisted improvements you&apos;ve used this billing month
                          </Text>
                          <ProgressBar
                            progress={
                              status.monthlyLlmLimit === 0
                                ? 0
                                : Math.min((status.monthlyLlmUsed / status.monthlyLlmLimit) * 100, 100)
                            }
                            size="small"
                          />
                        </div>

                        <div className="bill-quota-tile">
                          <Text as="p" variant="bodySm" tone="subdued">Trend history</Text>
                          <Text as="p" variant="bodyMd">{status.historyDays} days</Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            How far back you can track discoverability progress
                          </Text>
                        </div>

                        {status.features.crawlSimulation && (
                          <div className="bill-quota-tile">
                            <Text as="p" variant="bodySm" tone="subdued">Deep simulations used</Text>
                            <Text as="p" variant="bodyMd">
                              {status.deepSimMonthlyUsed} / {status.deepSimMonthlyLimit}
                            </Text>
                            <Text as="p" variant="bodySm" tone="subdued">
                              Deep AI simulations run this billing month
                            </Text>
                            <ProgressBar
                              progress={
                                status.deepSimMonthlyLimit === 0
                                  ? 0
                                  : Math.min((status.deepSimMonthlyUsed / status.deepSimMonthlyLimit) * 100, 100)
                              }
                              size="small"
                            />
                          </div>
                        )}
                      </div>

                      {currentTier !== 'FREE' && status.activatedAt && (
                        <div className="bill-current-plan-story">
                          <BlockStack gap="100">
                            <Text as="p" variant="bodySm" tone="subdued">📌 Start date</Text>
                            <Text as="p" variant="bodyMd">{formatDate(status.activatedAt)}</Text>
                          </BlockStack>
                          <BlockStack gap="100">
                            <Text as="p" variant="bodySm" tone="subdued">Best if you&apos;re...</Text>
                            <Text as="p" variant="bodyMd">{currentStory.bestFor}</Text>
                          </BlockStack>
                        </div>
                      )}

                      {status.monthlyLlmLimit > 0 && status.monthlyLlmUsed >= status.monthlyLlmLimit && (
                        <Banner tone="warning">
                          <Text as="p" variant="bodySm">
                            You&apos;ve used all {status.monthlyLlmLimit} AI score boosts this month.
                            {currentTier !== 'SCALE' && ' Upgrade for more boost capacity.'}
                          </Text>
                        </Banner>
                      )}

                      {status.status === 'FROZEN' && (
                        <Banner tone="warning">
                          <Text as="p" variant="bodySm">
                            Your subscription is frozen due to a payment issue. Features are limited to the Free plan.
                          </Text>
                        </Banner>
                      )}

                      {status.status === 'CANCELLED' && (
                        <Banner tone="warning">
                          <Text as="p" variant="bodySm">
                            Your subscription has been cancelled. You&apos;ll keep {TIER_LABELS[currentTier]} access
                            {status.currentPeriodEnd ? ` until ${formatDate(status.currentPeriodEnd)}` : ''}, then revert to the Free plan.
                          </Text>
                        </Banner>
                      )}

                      {status.couponExpiresAt && (
                        <Banner tone="info">
                          <Text as="p" variant="bodySm">
                            🎟️ Coupon active — {TIER_LABELS[currentTier]} access expires {formatDate(status.couponExpiresAt)}.
                          </Text>
                        </Banner>
                      )}
                    </BlockStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <InlineStack align="space-between" blockAlign="start">
                    <BlockStack gap="200">
                      <Text as="h2" variant="headingLg">
                        Choose How Aggressively Zelium Works For You
                      </Text>
                      <Text as="p" variant="bodyMd" tone="subdued">
                        Compare each plan by catalog coverage, monthly AI score boosts, and the automation layer you unlock.
                      </Text>
                    </BlockStack>
                    <ChoiceList
                      title=""
                      titleHidden
                      choices={[
                        { label: 'Monthly', value: 'monthly' },
                        { label: 'Annual (Save 20%)', value: 'annual' },
                      ]}
                      selected={billingInterval}
                      onChange={setBillingInterval}
                    />
                  </InlineStack>

                  <div className="bill-plan-grid">
                    {sortedPlans.map((plan) => (
                      <PlanCard
                        key={plan.tier}
                        plan={plan}
                        currentTier={currentTier}
                        currentInterval={currentInterval}
                        isAnnual={isAnnual}
                        subscriptionStatus={status?.status}
                        onPlanAction={handlePlanAction}
                        onDowngradeToFree={() => setCancelModalOpen(true)}
                        isSubscribePending={subscribeMutation.isPending}
                        isChangePending={changeTierMutation.isPending}
                      />
                    ))}
                  </div>

                  {currentTier === 'FREE' && (
                    <InlineStack align="center">
                      <Button
                        variant="plain"
                        onClick={() => setCouponModalOpen(true)}
                      >
                        Have a coupon?
                      </Button>
                    </InlineStack>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

        {/* Cancel subscription modal */}
        <Modal
          open={cancelModalOpen}
          onClose={() => setCancelModalOpen(false)}
          title="Cancel subscription?"
          primaryAction={{
            content: 'Cancel subscription',
            destructive: true,
            onAction: handleCancel,
            loading: cancelMutation.isPending,
          }}
          secondaryActions={[
            {
              content: 'Keep subscription',
              onAction: () => setCancelModalOpen(false),
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                Your access will continue until the end of the current billing period.
                After that, you&apos;ll revert to the Free plan.
              </Text>
              <Text as="p" variant="bodyMd">
                Your data will be retained but premium features will be read-only.
              </Text>
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Coupon redemption modal */}
        <Modal
          open={couponModalOpen}
          onClose={handleCloseCouponModal}
          title="Apply coupon"
          primaryAction={
            couponSuccess
              ? { content: 'Done', onAction: handleCloseCouponModal }
              : { content: 'Apply coupon', onAction: handleRedeemCoupon, loading: redeemCouponMutation.isPending }
          }
          secondaryActions={
            couponSuccess
              ? []
              : [{ content: 'Cancel', onAction: handleCloseCouponModal }]
          }
        >
          <Modal.Section>
            {couponSuccess ? (
              <BlockStack gap="300">
                <Banner tone="success">
                  <BlockStack gap="100">
                    <Text as="p" variant="bodyMd" fontWeight="semibold">
                      🎉 Coupon applied successfully!
                    </Text>
                    <Text as="p" variant="bodySm">
                      Plan: {TIER_LABELS[couponSuccess.tier]}
                    </Text>
                    <Text as="p" variant="bodySm">
                      Access expires: {formatDate(couponSuccess.couponExpiresAt)}
                    </Text>
                  </BlockStack>
                </Banner>
              </BlockStack>
            ) : (
              <BlockStack gap="300">
                <TextField
                  label="Coupon code"
                  value={couponCode}
                  onChange={(value) => { setCouponCode(value); setCouponError(''); }}
                  placeholder="Enter your coupon code"
                  autoComplete="off"
                  error={couponError || undefined}
                  onBlur={() => { /* noop */ }}
                />
                <Text as="p" variant="bodySm" tone="subdued">
                  Coupons grant temporary access to a paid plan at no cost. They can only be used once.
                </Text>
              </BlockStack>
            )}
          </Modal.Section>
        </Modal>
      </Layout>
      </BlockStack>
    </Page>
    </PageEntrance>
  );
}

export default BillingPage;
